/**
 * /invite/:token — Phase 4 Stream I (AcceptInvitePage).
 *
 * Landing page customers reach when clicking the invitation email link.
 * Three steps:
 *
 *   1. On mount, hit `GET /api/portal/invite/:token` to fetch the preview
 *      (customer name, email, expiry, status). The token comes from the
 *      route param so the SPA never reads it from `?query=`. The preview
 *      panel never renders the raw token.
 *   2. The user picks an activation flow:
 *        - password   → POST /consume with `password`, then redirect to
 *                       `/login?email=<consumed_email>` so they sign in
 *                       through the canonical form.
 *        - magic link → POST /consume with `request_magic_link: true`,
 *                       then show a terminal "check your email" panel.
 *   3. On any terminal error (expired / consumed / revoked / 404), show a
 *      tailored message with a single CTA back to `/login`. The page
 *      intentionally does NOT distinguish "unknown token" from "expired"
 *      in its server calls — the SPA only relies on the lookup's `status`.
 *
 * Standalone (NO AppShell, NO RequireAuth) — the user has no session yet.
 * App.tsx must mount the route outside of every `<RequireAuth>` wrapper.
 *
 * Japanese only for the Phase 4 MVP (psm portal/src/pages/AcceptInvitePage
 * shipped 10 locales inline; full i18n migration is deferred to
 * post-sprint per the Phase 1 inventory note).
 */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { PortalApiError } from '../lib/apiClient';
import { consumeInvitation, lookupInvitation, type InviteConsumeResult } from '../lib/invite';
import type {
  PortalInvitationLocale,
  PortalInvitationLookup,
  PortalInvitationStatus,
} from '@customer-portal/shared';

// ---------------------------------------------------------------------------
// Copy — Japanese only for Phase 4 MVP. Keep keys descriptive so the
// post-sprint i18n migration can swap out the literal strings without
// renaming code paths.
// ---------------------------------------------------------------------------

const COPY = {
  pageTitle: 'Activate your account',
  loading: 'Loading your invitation…',
  expiredTitle: 'Invitation link expired',
  expiredBody:
    'This invitation link has expired. Please ask your representative for a new invitation.',
  consumedTitle: 'This invitation has already been used',
  consumedBody: 'This account has already been activated. Please sign in from the sign-in page.',
  revokedTitle: 'This invitation is no longer valid',
  revokedBody: 'This invitation link has been revoked. Please contact your representative.',
  notFoundTitle: 'Invitation link not found',
  notFoundBody:
    'This invitation link is incorrect or has already expired. Please check the link in your email.',
  upstreamUnavailableTitle: 'Temporarily unavailable',
  upstreamUnavailableBody:
    'A temporary system issue has occurred. Please wait a moment and try again.',
  networkErrorTitle: 'A connection error occurred',
  networkErrorBody: 'Please check your network connection and try again.',
  invitedAs: 'Invited as',
  expiresAt: 'Expires',
  invitedBy: 'Invited by',
  passwordLabel: 'Password (12–128 characters)',
  passwordPlaceholder: 'Use a mix of letters, numbers, and symbols',
  passwordMismatch: 'Passwords do not match.',
  passwordTooShort: 'Password must be at least 12 characters.',
  passwordTooLong: 'Password must be 128 characters or fewer.',
  passwordConfirmLabel: 'Password (confirm)',
  setPasswordCta: 'Set password and activate',
  magicLinkCta: 'Get a sign-in link by email',
  setPasswordPanel: 'Set a password',
  magicLinkPanel: 'Sign in by email',
  rateLimitedMessage: 'Too many attempts. Please wait a while and try again.',
  validationFailed: 'Please check your input.',
  consumeNetworkError: 'A connection error occurred. Please try again.',
  consumeUpstreamUnavailable:
    'A temporary system issue has occurred. Please wait a moment and try again.',
  magicLinkSentTitle: 'Email sent',
  magicLinkSentBody:
    'We have sent you a sign-in link. Please check your email. The link is valid for 15 minutes.',
  redirectingToLogin: 'Taking you to the sign-in page…',
  goToLogin: 'Go to sign-in page',
} as const;

// ---------------------------------------------------------------------------
// State machine
// ---------------------------------------------------------------------------

type LookupState =
  | { kind: 'loading' }
  | { kind: 'ready'; preview: PortalInvitationLookup }
  | { kind: 'terminal'; status: PortalInvitationStatus | 'not_found' }
  | { kind: 'error'; title: string; body: string };

type ConsumeState =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'magic-link-sent' }
  | { kind: 'redirecting'; email: string }
  | { kind: 'error'; message: string };

type Flow = 'password' | 'magic_link';

const REDIRECT_AFTER_MS = 1200;

// Phase 4 MVP — the page hard-codes `ja` for the locale field so PSM
// stores a sensible default. The post-sprint i18n migration will add a
// locale picker that mutates this value.
const HARDCODED_LOCALE: PortalInvitationLocale = 'ja';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AcceptInvitePage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const tokenRef = useRef<string | null>(null);
  const [lookup, setLookup] = useState<LookupState>({ kind: 'loading' });
  const [flow, setFlow] = useState<Flow>('password');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [consume, setConsume] = useState<ConsumeState>({ kind: 'idle' });

  // -------------------------------------------------------------------------
  // Lookup on mount.
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!token || typeof token !== 'string' || token.length < 16) {
      setLookup({ kind: 'terminal', status: 'not_found' });
      return;
    }
    // Hold the token in a ref so it doesn't accidentally land in component
    // state / DevTools props / DOM nodes.
    tokenRef.current = token;
    const ctrl = new AbortController();
    void (async () => {
      try {
        const res = await lookupInvitation(token, ctrl.signal);
        // Short-circuit on terminal statuses so the page never renders the
        // activation form for an invitation that cannot succeed.
        if (res.data.status !== 'pending') {
          setLookup({ kind: 'terminal', status: res.data.status });
          return;
        }
        // Also short-circuit on a client-side expiry check — the server
        // remains the source of truth, but we want a fast path for users
        // sitting in their inbox for a week.
        const expiresAtMs = new Date(res.data.expires_at).getTime();
        if (Number.isFinite(expiresAtMs) && expiresAtMs <= Date.now()) {
          setLookup({ kind: 'terminal', status: 'expired' });
          return;
        }
        setLookup({ kind: 'ready', preview: res.data });
      } catch (err) {
        if (err instanceof PortalApiError) {
          if (err.status === 404) {
            setLookup({ kind: 'terminal', status: 'not_found' });
            return;
          }
          if (err.status === 429) {
            setLookup({
              kind: 'error',
              title: COPY.upstreamUnavailableTitle,
              body: COPY.rateLimitedMessage,
            });
            return;
          }
          if (err.status === 503) {
            setLookup({
              kind: 'error',
              title: COPY.upstreamUnavailableTitle,
              body: COPY.upstreamUnavailableBody,
            });
            return;
          }
        }
        setLookup({
          kind: 'error',
          title: COPY.networkErrorTitle,
          body: COPY.networkErrorBody,
        });
      }
    })();
    return () => ctrl.abort();
  }, [token]);

  // -------------------------------------------------------------------------
  // Consume submit.
  // -------------------------------------------------------------------------
  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    if (consume.kind === 'submitting') return;
    const t = tokenRef.current;
    if (!t || lookup.kind !== 'ready') return;

    if (flow === 'password') {
      if (password.length < 12) {
        setConsume({ kind: 'error', message: COPY.passwordTooShort });
        return;
      }
      if (password.length > 128) {
        setConsume({ kind: 'error', message: COPY.passwordTooLong });
        return;
      }
      if (password !== passwordConfirm) {
        setConsume({ kind: 'error', message: COPY.passwordMismatch });
        return;
      }
    }

    setConsume({ kind: 'submitting' });
    try {
      let result: InviteConsumeResult;
      if (flow === 'password') {
        result = await consumeInvitation(t, {
          locale: HARDCODED_LOCALE,
          password,
        });
      } else {
        result = await consumeInvitation(t, {
          locale: HARDCODED_LOCALE,
          request_magic_link: true,
        });
      }
      // Clear the captured token now that consume succeeded — even on
      // retry the upstream returns 404 (consumed), so further calls are
      // moot.
      tokenRef.current = null;
      setPassword('');
      setPasswordConfirm('');
      if (result.magic_link_sent === true || flow === 'magic_link') {
        setConsume({ kind: 'magic-link-sent' });
        return;
      }
      setConsume({ kind: 'redirecting', email: result.email });
      window.setTimeout(() => {
        navigate(`/login?email=${encodeURIComponent(result.email)}`, { replace: true });
      }, REDIRECT_AFTER_MS);
    } catch (err) {
      if (err instanceof PortalApiError) {
        if (err.status === 429) {
          setConsume({ kind: 'error', message: COPY.rateLimitedMessage });
          return;
        }
        if (err.status === 400) {
          setConsume({ kind: 'error', message: COPY.validationFailed });
          return;
        }
        if (err.status === 404) {
          // The invitation was consumed / revoked / expired between the
          // lookup and the consume call. Drop the user back to the
          // terminal state.
          setLookup({ kind: 'terminal', status: 'not_found' });
          return;
        }
        if (err.status === 503) {
          setConsume({
            kind: 'error',
            message: COPY.consumeUpstreamUnavailable,
          });
          return;
        }
      }
      setConsume({ kind: 'error', message: COPY.consumeNetworkError });
    }
  }

  // -------------------------------------------------------------------------
  // Rendering
  // -------------------------------------------------------------------------
  return (
    <section
      style={{
        maxWidth: '32rem',
        margin: '4rem auto',
        padding: '0 1.25rem',
      }}
      data-testid="accept-invite-root"
    >
      <h1 style={{ fontSize: '1.5rem', marginBottom: '1rem' }}>{COPY.pageTitle}</h1>

      {lookup.kind === 'loading' ? (
        <p data-testid="invite-loading" aria-busy="true">
          {COPY.loading}
        </p>
      ) : null}

      {lookup.kind === 'terminal' ? <TerminalPanel status={lookup.status} /> : null}

      {lookup.kind === 'error' ? <ErrorPanel title={lookup.title} body={lookup.body} /> : null}

      {lookup.kind === 'ready' && consume.kind === 'magic-link-sent' ? (
        <MagicLinkSentPanel />
      ) : null}

      {lookup.kind === 'ready' && consume.kind !== 'magic-link-sent' ? (
        <ActivationPanel
          preview={lookup.preview}
          flow={flow}
          onFlowChange={(next) => {
            setFlow(next);
            setConsume({ kind: 'idle' });
          }}
          password={password}
          onPasswordChange={setPassword}
          passwordConfirm={passwordConfirm}
          onPasswordConfirmChange={setPasswordConfirm}
          onSubmit={handleSubmit}
          submitting={consume.kind === 'submitting'}
          redirecting={consume.kind === 'redirecting' ? consume.email : null}
          errorMessage={consume.kind === 'error' ? consume.message : null}
        />
      ) : null}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function TerminalPanel({ status }: { status: PortalInvitationStatus | 'not_found' }) {
  let title: string = COPY.notFoundTitle;
  let body: string = COPY.notFoundBody;
  if (status === 'expired') {
    title = COPY.expiredTitle;
    body = COPY.expiredBody;
  } else if (status === 'consumed') {
    title = COPY.consumedTitle;
    body = COPY.consumedBody;
  } else if (status === 'revoked') {
    title = COPY.revokedTitle;
    body = COPY.revokedBody;
  }
  return (
    <div data-testid="invite-terminal" data-status={status}>
      <h2 style={{ fontSize: '1.15rem', marginBottom: '0.5rem' }}>{title}</h2>
      <p style={{ marginBottom: '1rem' }}>{body}</p>
      <Link to="/login" data-testid="invite-terminal-cta">
        {COPY.goToLogin}
      </Link>
    </div>
  );
}

function ErrorPanel({ title, body }: { title: string; body: string }) {
  return (
    <div role="alert" data-testid="invite-error">
      <h2 style={{ fontSize: '1.15rem', marginBottom: '0.5rem' }}>{title}</h2>
      <p style={{ marginBottom: '1rem' }}>{body}</p>
      <Link to="/login">{COPY.goToLogin}</Link>
    </div>
  );
}

function MagicLinkSentPanel() {
  return (
    <div data-testid="invite-magic-link-sent">
      <h2 style={{ fontSize: '1.15rem', marginBottom: '0.5rem' }}>{COPY.magicLinkSentTitle}</h2>
      <p style={{ marginBottom: '1rem' }}>{COPY.magicLinkSentBody}</p>
      <Link to="/login">{COPY.goToLogin}</Link>
    </div>
  );
}

function ActivationPanel({
  preview,
  flow,
  onFlowChange,
  password,
  onPasswordChange,
  passwordConfirm,
  onPasswordConfirmChange,
  onSubmit,
  submitting,
  redirecting,
  errorMessage,
}: {
  preview: PortalInvitationLookup;
  flow: Flow;
  onFlowChange: (next: Flow) => void;
  password: string;
  onPasswordChange: (next: string) => void;
  passwordConfirm: string;
  onPasswordConfirmChange: (next: string) => void;
  onSubmit: (e: FormEvent<HTMLFormElement>) => void;
  submitting: boolean;
  redirecting: string | null;
  errorMessage: string | null;
}) {
  // Render preview info — never the raw token.
  return (
    <>
      <dl
        data-testid="invite-preview"
        style={{
          display: 'grid',
          gridTemplateColumns: 'auto 1fr',
          gap: '0.5rem 1rem',
          marginBottom: '1.5rem',
        }}
      >
        <dt style={{ fontWeight: 600 }}>{COPY.invitedAs}</dt>
        <dd>
          {preview.customer_name ? `${preview.customer_name} — ` : ''}
          <span data-testid="invite-preview-email">{preview.email}</span>
        </dd>
        <dt style={{ fontWeight: 600 }}>{COPY.expiresAt}</dt>
        <dd data-testid="invite-preview-expires-at">{formatExpiresAt(preview.expires_at)}</dd>
        {preview.invited_by_name ? (
          <>
            <dt style={{ fontWeight: 600 }}>{COPY.invitedBy}</dt>
            <dd>{preview.invited_by_name}</dd>
          </>
        ) : null}
      </dl>

      <fieldset
        style={{
          border: '1px solid #ddd',
          borderRadius: '0.25rem',
          padding: '0.75rem 1rem 1rem',
          marginBottom: '1rem',
        }}
      >
        <legend style={{ fontWeight: 600 }}>{COPY.pageTitle}</legend>
        <label style={{ display: 'block', marginTop: '0.5rem' }}>
          <input
            type="radio"
            name="invite-flow"
            value="password"
            checked={flow === 'password'}
            onChange={() => onFlowChange('password')}
            disabled={submitting}
          />{' '}
          {COPY.setPasswordPanel}
        </label>
        <label style={{ display: 'block', marginTop: '0.25rem' }}>
          <input
            type="radio"
            name="invite-flow"
            value="magic_link"
            checked={flow === 'magic_link'}
            onChange={() => onFlowChange('magic_link')}
            disabled={submitting}
          />{' '}
          {COPY.magicLinkPanel}
        </label>
      </fieldset>

      <form onSubmit={onSubmit} noValidate>
        {flow === 'password' ? (
          <>
            <label
              htmlFor="invite-password"
              style={{
                display: 'block',
                marginBottom: '0.25rem',
                fontWeight: 600,
              }}
            >
              {COPY.passwordLabel}
            </label>
            <input
              id="invite-password"
              type="password"
              autoComplete="new-password"
              required
              minLength={12}
              maxLength={128}
              value={password}
              onChange={(e) => onPasswordChange(e.target.value)}
              placeholder={COPY.passwordPlaceholder}
              disabled={submitting}
              style={{
                display: 'block',
                width: '100%',
                padding: '0.5rem',
                marginBottom: '0.5rem',
              }}
            />
            <label
              htmlFor="invite-password-confirm"
              style={{
                display: 'block',
                marginBottom: '0.25rem',
                fontWeight: 600,
              }}
            >
              {COPY.passwordConfirmLabel}
            </label>
            <input
              id="invite-password-confirm"
              type="password"
              autoComplete="new-password"
              required
              minLength={12}
              maxLength={128}
              value={passwordConfirm}
              onChange={(e) => onPasswordConfirmChange(e.target.value)}
              disabled={submitting}
              style={{
                display: 'block',
                width: '100%',
                padding: '0.5rem',
                marginBottom: '0.5rem',
              }}
            />
          </>
        ) : null}

        {errorMessage ? (
          <div
            role="alert"
            data-testid="invite-consume-error"
            style={{ color: 'crimson', marginBottom: '0.5rem' }}
          >
            {errorMessage}
          </div>
        ) : null}
        {redirecting ? (
          <p data-testid="invite-redirecting" role="status" style={{ marginBottom: '0.5rem' }}>
            {COPY.redirectingToLogin}
          </p>
        ) : null}

        <button
          type="submit"
          disabled={submitting || redirecting !== null}
          data-testid="invite-submit"
          style={{
            padding: '0.5rem 1rem',
            background: '#1f6feb',
            color: 'white',
            border: 0,
            borderRadius: '0.25rem',
            cursor: submitting ? 'wait' : 'pointer',
          }}
        >
          {flow === 'password' ? COPY.setPasswordCta : COPY.magicLinkCta}
        </button>
      </form>
    </>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatExpiresAt(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    // Locale-friendly but explicit so the test harness sees a deterministic
    // string regardless of the runner's locale.
    return d.toISOString().replace('T', ' ').slice(0, 16) + ' UTC';
  } catch {
    return iso;
  }
}
